import { useEffect, useState, useContext } from "react";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import "../categoriasFinanceiro/CategoriasFinanceiro.scss";
import { AuthContext } from "../../../shared/context/auth-context";
import { Popconfirm } from "antd";
import ErrorIcon from "@mui/icons-material/Error";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotaPartilhaUnidades from "../components/NotaPartilhaUnidades";

function Shortcuts({ sendRequest, clinicaId }) {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nomeShortcut, setNomeShortcut] = useState("");
  const [tratamentosSelecionados, setTratamentosSelecionados] = useState([]);
  const [tratamentoAtual, setTratamentoAtual] = useState(""); // Tratamento sendo adicionado
  const [quantidadeAtual, setQuantidadeAtual] = useState(1); // Quantidade do tratamento atual
  const [openModal, setOpenModal] = useState(false);
  const [idShortcut, setIdShortcut] = useState(null);
  const [shortcuts, setShortcuts] = useState([]);
  const [tratamentos, setTratamentos] = useState([]);
  const [mensagemDeErro, setMensagemDeErro] = useState("");
  const auth = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        // Buscar shortcuts e tratamentos em paralelo
        const [shortcutsResponse, tratamentosResponse] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/shortcutsavaliacao/${clinicaId}`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/populated/${clinicaId}`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          ),
        ]);

        setShortcuts(shortcutsResponse.shortcuts || []);
        setTratamentos(tratamentosResponse.tratamentos || []);
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
      } finally {
        stopLoading();
      }
    };
    fetchData();
  }, [auth.token, clinicaId, sendRequest]);

  const nomeShortcutChangeHandler = (e) => {
    setNomeShortcut(e.target.value);
  };

  const adicionarTratamentoHandler = () => {
    if (!tratamentoAtual) {
      setMensagemDeErro("Selecione um tratamento");
      return;
    }

    if (quantidadeAtual < 1) {
      setMensagemDeErro("A quantidade deve ser pelo menos 1");
      return;
    }

    setTratamentosSelecionados((current) => [
      ...current,
      {
        tratamento: tratamentoAtual,
        quantidade: quantidadeAtual,
      },
    ]);

    // Reset
    setTratamentoAtual("");
    setQuantidadeAtual(1);
    setMensagemDeErro("");
  };

  const removerTratamentoHandler = (index) => {
    setTratamentosSelecionados((current) =>
      current.filter((_, i) => i !== index)
    );
  };

  const guardarShortcut = async () => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("nome", nomeShortcut);
      formData.append("tratamentos", JSON.stringify(tratamentosSelecionados));
      formData.append("clinica", clinicaId);
      formData.append("criadoPor", auth.userId);

      const shortcutCriado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/shortcutsavaliacao/`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setShortcuts((current) => [...current, shortcutCriado.shortcut]);

      setNomeShortcut("");
      setTratamentosSelecionados([]);
      setOpenModal(false);
      setIdShortcut(null);

      toast.success("Atalho criado com sucesso");
    } catch (err) {
      console.error("Erro ao criar atalho:", err);
      toast.error("Erro ao criar atalho");
    } finally {
      stopLoading();
    }
    setIsSubmitting(false);
  };

  const editarShortcut = async (id) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("nome", nomeShortcut);
      formData.append("tratamentos", JSON.stringify(tratamentosSelecionados));
      formData.append("atualizadoPor", auth.userId);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/shortcutsavaliacao/${id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      const shortcutsCopy = [...shortcuts];
      const shortcutsModificados = shortcutsCopy.map((shortcut) => {
        if (shortcut.id === id) {
          return {
            ...shortcut,
            nome: nomeShortcut,
            tratamentos: tratamentosSelecionados,
          };
        }
        return shortcut;
      });

      setShortcuts([...shortcutsModificados]);

      setNomeShortcut("");
      setTratamentosSelecionados([]);
      setIdShortcut(null);
      setOpenModal(false);

      toast.success("Atalho modificado com sucesso");
    } catch (err) {
      console.error("Erro ao editar atalho:", err);
      toast.error("Erro ao editar atalho");
    } finally {
      stopLoading();
    }
    setIsSubmitting(false);
  };

  const preEditarShortcut = async (id) => {
    const shortcutParaEditar = shortcuts.find((s) => s.id === id);
    setIdShortcut(id);
    setNomeShortcut(shortcutParaEditar.nome);
    // Converter para novo formato se necessário
    setTratamentosSelecionados(
      shortcutParaEditar.tratamentos.map((t) => ({
        tratamento: t.tratamento?.id || t.tratamento || t.id || t,
        quantidade: t.quantidade || 1,
      }))
    );
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setMensagemDeErro("");
    setIdShortcut(null);
    setNomeShortcut("");
    setTratamentosSelecionados([]);
    setTratamentoAtual("");
    setQuantidadeAtual(1);
    setOpenModal(false);
  };

  const desativarShortcut = async (id) => {
    startLoading();
    try {
      const formDataDesativar = new FormData();
      formDataDesativar.append("utilizador", auth.userId);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/shortcutsavaliacao/toggleativo/${id}`,
        "PATCH",
        formDataDesativar,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setShortcuts((current) => current.filter((s) => s.id !== id));

      toast.success("Atalho desativado com sucesso");
    } catch (err) {
      console.error("Erro ao desativar atalho:", err);
      toast.error("Erro ao desativar atalho");
    } finally {
      stopLoading();
    }
  };

  const beforeSubmit = (idShortcut) => {
    let filteredShortcut;
    let duplicidadeShortcut = false;

    if (idShortcut) {
      filteredShortcut = shortcuts.find((s) => s.id === idShortcut);
      duplicidadeShortcut = shortcuts.filter(
        (s) => s.nome.toLowerCase() === nomeShortcut.trim().toLowerCase()
      );
    } else {
      duplicidadeShortcut = shortcuts.filter(
        (s) => s.nome.toLowerCase() === nomeShortcut.trim().toLowerCase()
      );
    }

    // Se o nome permanecer igual e os tratamentos também, não modificamos nada
    if (
      idShortcut &&
      filteredShortcut?.nome?.toLowerCase() ===
        nomeShortcut.trim().toLowerCase() &&
      JSON.stringify(
        filteredShortcut.tratamentos.map((t) => t.id || t).sort()
      ) === JSON.stringify(tratamentosSelecionados.sort())
    ) {
      setNomeShortcut("");
      setTratamentosSelecionados([]);
      setIdShortcut(null);
      setOpenModal(false);
    } else {
      let haErro = false;

      if (
        duplicidadeShortcut.length > 0 &&
        (!idShortcut || duplicidadeShortcut[0].id !== idShortcut)
      ) {
        setMensagemDeErro("Já existe um atalho com esse nome.");
        haErro = true;
      } else if (!nomeShortcut || nomeShortcut.trim() === "") {
        setMensagemDeErro("Introduza um nome para o atalho.");
        haErro = true;
      } else if (tratamentosSelecionados.length === 0) {
        setMensagemDeErro("Adicione pelo menos um tratamento.");
        haErro = true;
      } else {
        setMensagemDeErro("");
      }

      if (!isSubmitting && !haErro) {
        setIsSubmitting(true);
        if (idShortcut) {
          editarShortcut(idShortcut);
        } else {
          guardarShortcut();
        }
      }
    }
  };

  const getTratamentoNome = (tratamentoId) => {
    // Suporta tanto ID direto quanto objeto {tratamento: ID}
    const id =
      typeof tratamentoId === "object" ? tratamentoId.tratamento : tratamentoId;
    const tratamento = tratamentos.find((t) => t.id === id);
    return tratamento ? tratamento.designacao : id || "Tratamento desconhecido";
  };

  return (
    <>
      <div className="categoriasGeral__container">
        {(auth.role === "Médico/Administrador" || auth.role === "Gerente") && (
          <div className="categoriasGeral__container__btn-adicionar">
            <Button
              variant="contained"
              color="success"
              onClick={() => setOpenModal(true)}
            >
              Novo Atalho
            </Button>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#f3e5f5",
            border: "1px solid #ce93d8",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            gap: "15px",
            alignItems: "flex-start",
            gridColumn: "1/-1",
          }}
        >
          <InfoOutlinedIcon style={{ color: "#7b1fa2", marginTop: "2px" }} />
          <div
            style={{ fontSize: "14px", color: "#6a1b9a", lineHeight: "1.6" }}
          >
            <strong
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "15px",
              }}
            >
              Como funcionam os Atalhos?
            </strong>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              <li>
                Crie combinações de tratamentos frequentes para{" "}
                <strong>agilizar a criação de avaliações</strong>.
              </li>
              <li>
                Com <strong>um único clique</strong>, todos os tratamentos do
                atalho são adicionados automaticamente.
              </li>
              <li>
                Pode definir a <strong>quantidade</strong> de cada tratamento no
                atalho (ex: "Restauração x4").
              </li>
              <NotaPartilhaUnidades porUnidade />
            </ul>
          </div>
        </div>

        {shortcuts &&
          shortcuts.map((shortcut, index) => (
            <div
              className="categoriasFinanceiro__container__categoria"
              key={index}
            >
              <div className="categoriasFinanceiro__container__categoria--1">
                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                  {shortcut.nome}
                </div>
                <div style={{ fontSize: "0.9em", color: "#666" }}>
                  {shortcut.tratamentos && shortcut.tratamentos.length > 0
                    ? shortcut.tratamentos
                        .map((t) => {
                          const nome =
                            typeof t === "object"
                              ? t.tratamento?.designacao ||
                                getTratamentoNome(t.tratamento)
                              : getTratamentoNome(t);
                          const qtd = t.quantidade || 1;
                          return qtd > 1 ? `${nome} (${qtd}x)` : nome;
                        })
                        .join(", ")
                    : "Sem tratamentos"}
                </div>
              </div>
              <div className="categoriasFinanceiro__container__categoria--2">
                {(auth.role === "Médico/Administrador" ||
                  auth.role === "Gerente") && (
                  <>
                    <Popconfirm
                      title={`Desativar Atalho`}
                      description={`Pretende desativar esse atalho?`}
                      icon={<ErrorIcon style={{ color: "red" }} />}
                      okText="Sim"
                      cancelText="Não"
                      onConfirm={() => desativarShortcut(shortcut.id)}
                      className="popConfirm--biggerPadding"
                    >
                      <span>Desativar</span>
                    </Popconfirm>
                    <span onClick={() => preEditarShortcut(shortcut.id)}>
                      Editar
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}

        <Modal
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            className="fluxo-atendimento__modal__box adicionar-categoria__modal"
            id="modal__adicionar-categoria"
          >
            <h1 className="fluxo-atendimento__modal__titulo">
              {idShortcut ? "Editar Atalho" : "Novo Atalho"}
            </h1>
            <div className="adicionar-categoria__modal__container">
              <TextField
                fullWidth
                label="Nome do Atalho *"
                value={nomeShortcut}
                onChange={nomeShortcutChangeHandler}
                variant="outlined"
                style={{ marginBottom: "20px" }}
              />

              {/* Adicionar tratamento */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginBottom: "20px",
                  alignItems: "flex-start",
                }}
              >
                <FormControl style={{ flex: 1 }}>
                  <InputLabel id="tratamento-atual-label">
                    Tratamento
                  </InputLabel>
                  <Select
                    labelId="tratamento-atual-label"
                    value={tratamentoAtual}
                    onChange={(e) => setTratamentoAtual(e.target.value)}
                    input={<OutlinedInput label="Tratamento" />}
                  >
                    {tratamentos.map((tratamento) => (
                      <MenuItem key={tratamento.id} value={tratamento.id}>
                        <ListItemText
                          primary={tratamento.designacao}
                          secondary={tratamento.categoria?.categoria}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Quantidade"
                  type="number"
                  value={quantidadeAtual}
                  onChange={(e) =>
                    setQuantidadeAtual(parseInt(e.target.value) || 1)
                  }
                  inputProps={{ min: 1 }}
                  style={{ width: "100px" }}
                />

                <Button
                  variant="contained"
                  onClick={adicionarTratamentoHandler}
                  style={{ height: "56px" }}
                >
                  +
                </Button>
              </div>

              {/* Lista de tratamentos adicionados */}
              {tratamentosSelecionados.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      fontSize: "0.9em",
                      color: "#666",
                      marginBottom: "8px",
                    }}
                  >
                    Tratamentos adicionados ({tratamentosSelecionados.length}):
                  </div>
                  <div
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "10px",
                    }}
                  >
                    {tratamentosSelecionados.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px",
                          borderBottom:
                            index < tratamentosSelecionados.length - 1
                              ? "1px solid #eee"
                              : "none",
                        }}
                      >
                        <span>
                          {getTratamentoNome(item.tratamento)}{" "}
                          {item.quantidade > 1 && `(${item.quantidade}x)`}
                        </span>
                        <button
                          onClick={() => removerTratamentoHandler(index)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#f44336",
                            cursor: "pointer",
                            fontSize: "1.2em",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {mensagemDeErro && (
              <div className="erroContainer__categoria-caixa">
                {mensagemDeErro}
              </div>
            )}

            <div className="fluxo-atendimento__modal__bottom_btns">
              <span
                className="cancel-btn"
                onClick={handleModalClose}
                style={{ display: "block" }}
              >
                Fechar
              </span>

              <span
                disabled={isSubmitting}
                className="blue-button"
                onClick={() => beforeSubmit(idShortcut)}
              >
                {isSubmitting ? "Guardando..." : "Guardar"}
              </span>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

export default Shortcuts;
